import React, {useState} from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import {useIntl} from 'react-intl';

import LoadingButton from '../form/LoadingButton';
import API from '../../common/utils/API';
import PaymentsInFlight from './PaymentsInFlight';

// El backend acepta motivos de hasta 500 caracteres; vacío se guarda como null.
const REASON_MAX_LENGTH = 500;

/**
 * Confirma el cambio de un kill switch. `target` trae la clave y el valor al
 * que se quiere llevar (`{key, enabled}`). El motivo es opcional (vacío se
 * manda como `null`). En `payments` muestra también las compras en curso, para
 * decidir con el dato a la vista. Si el PUT falla, el modal queda abierto con
 * el error y la lista no se toca.
 */
function KillSwitchConfirmModal({target, onHide, onConfirmed}) {
    const intl = useIntl();
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const trimmedReason = reason.trim();

    async function handleConfirm() {
        if (saving) return;

        setSaving(true);
        setError(null);

        try {
            const updated = await API.killSwitches.update(
                target.key,
                target.enabled,
                trimmedReason || null,
            );
            onConfirmed(target, updated);
        } catch (e) {
            const message =
                e && e.response && e.response.data && e.response.data.message;
            setError(
                message ||
                    intl.formatMessage({id: 'KILL_SWITCHES.UPDATE_ERROR'}),
            );
            setSaving(false);
        }
    }

    return (
        <Modal show onHide={onHide} data-test='killSwitchConfirmModal'>
            <Modal.Header closeButton>
                <Modal.Title>
                    {intl.formatMessage(
                        {
                            id: target.enabled
                                ? 'KILL_SWITCHES.MODAL_TITLE_ENABLE'
                                : 'KILL_SWITCHES.MODAL_TITLE_DISABLE',
                        },
                        {key: target.key},
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant='danger' data-test='killSwitchUpdateError'>
                        {error}
                    </Alert>
                )}
                {target.key === 'payments' && (
                    <div className='mb-3' data-test='killSwitchModalInFlight'>
                        <PaymentsInFlight
                            enabled={!target.enabled}
                            showSafeToDeploy={false}
                        />
                    </div>
                )}
                <Form.Group controlId='killSwitchReason'>
                    <Form.Label>
                        {intl.formatMessage({
                            id: 'KILL_SWITCHES.MODAL_REASON_LABEL',
                        })}
                    </Form.Label>
                    <Form.Control
                        as='textarea'
                        rows={3}
                        maxLength={REASON_MAX_LENGTH}
                        value={reason}
                        placeholder={intl.formatMessage({
                            id: 'KILL_SWITCHES.MODAL_REASON_PLACEHOLDER',
                        })}
                        onChange={(event) => setReason(event.target.value)}
                        data-test='killSwitchReasonInput'
                    />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant='secondary'
                    className='btn-bold btn-light-dark'
                    onClick={onHide}
                    disabled={saving}
                >
                    {intl.formatMessage({id: 'GENERAL.CANCEL'})}
                </Button>
                <LoadingButton
                    variant={target.enabled ? 'primary' : 'danger'}
                    className='btn-bold'
                    loading={saving}
                    disabled={saving}
                    onClick={handleConfirm}
                    data-test='killSwitchConfirmButton'
                >
                    {intl.formatMessage({id: 'KILL_SWITCHES.MODAL_CONFIRM'})}
                </LoadingButton>
            </Modal.Footer>
        </Modal>
    );
}

export default KillSwitchConfirmModal;
